import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// ── Shared primitives ─────────────────────────────────────────────────────
const S = {
  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 18, marginTop: 14 },
  h: { fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 },
  p: { fontSize: 14, color: "#334155", lineHeight: 1.55, margin: "8px 0 0" },
  muted: { fontSize: 12.5, color: "#64748b", lineHeight: 1.5, margin: "8px 0 0" },
  ol: { margin: "8px 0 0 18px", padding: 0, fontSize: 14, color: "#334155", lineHeight: 1.6, listStyle: "decimal" },
  link: { color: "#0f766e", fontWeight: 600, textDecoration: "underline" },
  select: { marginTop: 8, fontSize: 14, padding: "8px 10px", borderRadius: 10, border: "1px solid #cbd5e1", width: "100%", maxWidth: 420 },
};

export function useEmergencyContext() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let live = true;
    api.get("/api/emergency/context")
      .then(({ data }) => live && setState({ loading: false, data, error: null }))
      .catch((e) => live && setState({ loading: false, data: null, error: e?.response?.status === 404 ? "no-voyage" : "error" }));
    return () => { live = false; };
  }, []);
  return state;
}

function Phone({ value, label, testid }) {
  if (!value) return <span data-testid={testid} style={{ color: "#b45309", fontWeight: 600 }}>not on file</span>;
  return <a href={`tel:${String(value).replace(/[^+\d]/g, "")}`} style={S.link} data-testid={testid}>{label || value}</a>;
}

function Missing({ what, route, testid }) {
  return (
    <p style={S.muted} data-testid={testid}>
      No {what} saved yet. <a href={`#${route}`} style={S.link}>Add it here</a> so it appears in an emergency.
    </p>
  );
}

function Sources({ sources, verifiedOn }) {
  if (!sources?.length) return null;
  return (
    <p style={S.muted}>
      Source: {sources.map((s, i) => (<span key={s.url}>{i ? " · " : ""}<a href={s.url} target="_blank" rel="noreferrer" style={S.link}>{s.title}</a></span>))}
      {verifiedOn ? ` · checked ${verifiedOn}` : ""}
    </p>
  );
}

// Location is never inferred from the itinerary — the traveller chooses.
export function LocationPicker({ ctx, value, onChange }) {
  const ports = ctx?.voyage?.ports || [];
  return (
    <label style={{ display: "block", marginTop: 10, fontSize: 13, color: "#475569" }}>
      Where are you right now?
      <select style={S.select} value={value} onChange={(e) => onChange(e.target.value)} data-testid="cc-emergency-location">
        <option value="">Select your location…</option>
        <option value="home">Not yet travelling (home / en route)</option>
        <option value="onboard">On board {ctx?.voyage?.ship_name || "the ship"}</option>
        {ports.map((p) => (<option key={`${p.day}-${p.name}`} value={`port:${p.name}`}>{p.name}{p.date ? ` · ${p.date}` : ""}</option>))}
        <option value="other">Somewhere else</option>
      </select>
    </label>
  );
}

function InsuranceList({ ctx }) {
  const list = ctx?.insurance || [];
  if (!list.length) return <Missing what="travel-insurance policy" route="insurance" testid="cc-emergency-missing-insurance" />;
  return list.map((p) => (
    <p key={p.id} style={S.p} data-testid={`cc-emergency-insurance-${p.id}`}>
      <strong>{p.provider || "Insurer"}</strong> · policy {p.policy_number_masked || "on file"} — 24h assistance:{" "}
      <Phone value={p.emergency_phone} testid={`cc-emergency-insurance-phone-${p.id}`} />
      {p.covers_medical === false ? " · medical cover: not recorded" : ""}
      {p.covers_evacuation ? " · evacuation covered" : ""}
    </p>
  ));
}

function ContactsList({ ctx }) {
  const list = ctx?.emergency_contacts || [];
  if (!list.length) return <Missing what="emergency contact" route="travelers" testid="cc-emergency-missing-contacts" />;
  return list.map((c) => (
    <p key={c.id} style={S.p} data-testid={`cc-emergency-contact-${c.id}`}>
      <strong>{c.name}</strong>{c.relationship ? ` · ${c.relationship}` : ""} — <Phone value={c.phone} testid={`cc-emergency-contact-phone-${c.id}`} />
      {c.email ? <> · <a href={`mailto:${c.email}`} style={S.link}>{c.email}</a></> : null}
    </p>
  ));
}

function CruiseLineBlock({ ctx }) {
  const line = ctx?.cruise_line;
  const name = line?.name || ctx?.voyage?.line_name || "the cruise line";
  if (!line) {
    return <p style={S.muted} data-testid="cc-emergency-line-missing">No verified emergency number is on file for {name}. Use the number printed on your booking confirmation or the operator's official website — Cruise Command does not guess numbers.</p>;
  }
  return (
    <>
      {line.missed_ship.map((c) => (
        <p key={c.phone} style={S.p}><strong>{c.label}</strong> — <Phone value={c.phone} testid={`cc-emergency-line-phone-${c.phone.replace(/\D/g, "")}`} />{c.note ? <span style={{ color: "#64748b" }}> · {c.note}</span> : null}</p>
      ))}
      {line.email ? <p style={S.p}>Email: <a href={`mailto:${line.email}`} style={S.link}>{line.email}</a></p> : null}
      <Sources sources={line.sources} verifiedOn={line.verified_on} />
    </>
  );
}

// ── 1 · Medical: access logistics + saved assistance contacts only ────────
export function MedicalPanel({ ctx, location, setLocation }) {
  const onboard = location === "onboard";
  return (
    <div data-testid="cc-emergency-panel-medical">
      <LocationPicker ctx={ctx} value={location} onChange={setLocation} />
      <div style={S.card}>
        <h3 style={S.h}>Getting help</h3>
        <ol style={S.ol}>
          {onboard ? (<>
            <li>Dial the ship's emergency number from any cabin or deck phone (printed on the phone and your cabin card) — the medical centre is staffed 24 hours.</li>
            <li>Tell Guest Services your cabin number{ctx?.voyage?.cabin_number ? ` (${ctx.voyage.cabin_number})` : ""}; they coordinate with the medical centre.</li>
            <li>Ask the medical centre for an itemised invoice and report — your insurer needs both for a claim.</li>
          </>) : location.startsWith("port:") ? (<>
            <li>For a serious emergency call the local emergency number (112 works on GSM phones in most countries).</li>
            <li>Then call your insurer's 24h assistance line below — they direct you to an approved clinic and open a case.</li>
            <li>Tell the ship: Guest Services or the port agent number on your daily programme, so the ship knows where you are.</li>
          </>) : (<>
            <li>Before sailing, confirm your insurer's 24h assistance number is saved on your phone (below) and that prescriptions are packed in hand luggage.</li>
            <li>Onboard medical care is private and billed to your folio; keep receipts for your insurer.</li>
            <li>If you are unwell before departure, call the cruise line and your insurer before travelling — boarding may be refused for some conditions.</li>
          </>)}
        </ol>
        <p style={S.muted}>Cruise Command gives access steps and your saved contacts only — no diagnosis or treatment advice.</p>
      </div>
      <div style={S.card}><h3 style={S.h}>Your insurer's assistance line</h3><InsuranceList ctx={ctx} /></div>
      <div style={S.card}><h3 style={S.h}>Your emergency contacts</h3><ContactsList ctx={ctx} /></div>
    </div>
  );
}

// ── 2 · Lost passport: official consular resources by nationality ─────────
export function PassportPanel({ ctx, location, setLocation }) {
  const travelers = ctx?.travelers || [];
  const known = travelers.find((t) => t.nationality_code);
  const [code, setCode] = useState(known?.nationality_code || "");
  const res = ctx?.consular?.[code];
  const port = location.startsWith("port:") ? location.slice(5) : null;
  return (
    <div data-testid="cc-emergency-panel-passport">
      <LocationPicker ctx={ctx} value={location} onChange={setLocation} />
      <label style={{ display: "block", marginTop: 10, fontSize: 13, color: "#475569" }}>
        Passport nationality
        <select style={S.select} value={code} onChange={(e) => setCode(e.target.value)} data-testid="cc-emergency-nationality">
          <option value="">Select nationality…</option>
          {Object.entries(ctx?.consular || {}).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </label>
      {!known && <p style={S.muted} data-testid="cc-emergency-nationality-missing">No nationality is saved on your traveller profiles — <a href="#travelers" style={S.link}>add it</a> and this will be pre-selected next time.</p>}
      <div style={S.card}>
        <h3 style={S.h}>What to do</h3>
        <ol style={S.ol}>
          <li>Report the loss to the ship's Guest Services{port ? ` and the local police in ${port} (a police report is usually required for an emergency travel document)` : " (and local police if ashore)"}.</li>
          <li>Contact your embassy or consulate using the official finder below — they issue emergency travel documents; the ship cannot.</li>
          <li>Have a photo of the passport data page and a passport photo ready; your booking number{ctx?.voyage?.booking_number ? ` (${ctx.voyage.booking_number})` : ""} helps the line confirm your identity.</li>
          <li>Tell your insurer — many policies cover replacement costs.</li>
        </ol>
      </div>
      {res ? (
        <div style={S.card} data-testid="cc-emergency-consular">
          <h3 style={S.h}>{res.label} · official resources</h3>
          <p style={S.p}><a href={res.steps_url} target="_blank" rel="noreferrer" style={S.link}>Official lost/stolen passport guidance</a></p>
          <p style={S.p}><a href={res.find_embassy_url} target="_blank" rel="noreferrer" style={S.link}>Find the nearest embassy or consulate</a></p>
          <p style={S.p}>Emergency consular line: <strong>{res.hotline}</strong></p>
          <p style={S.muted}>Published by the government listed · checked {res.verified_on}. Confirm the number on the linked page before calling.</p>
        </div>
      ) : <p style={S.muted} data-testid="cc-emergency-consular-none">Select a nationality to see its official consular resources. Other nationalities: contact your country's foreign ministry — Cruise Command only lists sources it has verified.</p>}
    </div>
  );
}

// ── 3 · Missed the ship ───────────────────────────────────────────────────
export function MissedShipPanel({ ctx }) {
  const agents = ctx?.travel_agents || [];
  return (
    <div data-testid="cc-emergency-panel-missed-ship">
      <div style={S.card}>
        <h3 style={S.h}>Do this, in order</h3>
        <ol style={S.ol}>
          <li>Call the cruise line's emergency team now (below) with your booking number{ctx?.voyage?.booking_number ? ` ${ctx.voyage.booking_number}` : ""}. Only they can say whether you may join at a later port — it is not guaranteed.</li>
          <li>If you are at the pier, find the port agent (the ship's local representative; the name and number are on the daily programme) — they hold passports left behind and relay to the ship.</li>
          <li>Call your insurer's assistance line: missed-departure and onward-travel costs may be covered.</li>
          <li>Keep every receipt; do not book onward travel until the line confirms a boarding port.</li>
        </ol>
      </div>
      <div style={S.card}><h3 style={S.h}>Cruise line</h3><CruiseLineBlock ctx={ctx} /></div>
      <div style={S.card}>
        <h3 style={S.h}>Travel agent</h3>
        {agents.length ? agents.map((a) => (
          <p key={a.id} style={S.p} data-testid={`cc-emergency-agent-${a.id}`}><strong>{a.agency_name || a.advisor_name}</strong> — after-hours: <Phone value={a.emergency_phone} testid={`cc-emergency-agent-emergency-${a.id}`} /> · office: <Phone value={a.advisor_phone} testid={`cc-emergency-agent-office-${a.id}`} /></p>
        )) : <Missing what="travel agent" route="travelers" testid="cc-emergency-missing-agent" />}
      </div>
      <div style={S.card}><h3 style={S.h}>Insurer</h3><InsuranceList ctx={ctx} /></div>
    </div>
  );
}

// ── 4 · Emergency contacts (everything saved, missing entries labelled) ───
export function ContactsPanel({ ctx }) {
  return (
    <div data-testid="cc-emergency-panel-contacts">
      <div style={S.card}><h3 style={S.h}>Family & friends</h3><ContactsList ctx={ctx} /></div>
      <div style={S.card}><h3 style={S.h}>Insurance assistance</h3><InsuranceList ctx={ctx} /></div>
      <div style={S.card}><h3 style={S.h}>Cruise line</h3><CruiseLineBlock ctx={ctx} /></div>
      <div style={S.card}>
        <h3 style={S.h}>Ship & port agent</h3>
        <p style={S.muted} data-testid="cc-emergency-ship-contacts-missing">Ship and port-agent numbers are issued by the line on your daily programme and SetSail pass; Cruise Command does not store them yet. Photograph the daily programme each morning.</p>
      </div>
    </div>
  );
}

// ── 5 · Today's safety information · stage-aware, never a future port ─────
export function SafetyInfoPanel({ ctx }) {
  const v = ctx?.voyage || {};
  const today = new Date().toISOString().slice(0, 10);
  const todayPort = useMemo(() => (v.ports || []).find((p) => p.date === today), [v.ports, today]);
  return (
    <div data-testid="cc-emergency-panel-safety-info">
      <div style={S.card}>
        <h3 style={S.h}>{v.stage === "pre-voyage" ? "Before you sail" : v.stage === "post-voyage" ? "Voyage complete" : todayPort ? `Today · ${todayPort.name}` : "Today · at sea"}</h3>
        {v.stage === "pre-voyage" ? (
          <ol style={S.ol} data-testid="cc-emergency-safety-pre">
            <li>Save your insurer's 24h line and the cruise line's emergency team number (Emergency Contacts) on your phone.</li>
            <li>Register travel plans with your government's traveller programme if offered (US STEP, UK travel advice alerts, Canada ROCA, Australia Smartraveller).</li>
            <li>Check official travel advice for each port you will visit: <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noreferrer" style={S.link}>US State Department</a> · <a href="https://www.gov.uk/foreign-travel-advice" target="_blank" rel="noreferrer" style={S.link}>UK FCDO</a> · <a href="https://travel.gc.ca/travelling/advisories" target="_blank" rel="noreferrer" style={S.link}>Canada</a> · <a href="https://www.smartraveller.gov.au/destinations" target="_blank" rel="noreferrer" style={S.link}>Australia</a>.</li>
            <li>Pack prescriptions in hand luggage with a copy of the prescription; photograph passports and the booking confirmation.</li>
            <li>Attend the mandatory muster briefing on embarkation day{v.embark_date ? ` (${v.embark_date})` : ""} — it is required before sailing.</li>
          </ol>
        ) : v.stage === "in-voyage" ? (
          <ol style={S.ol} data-testid="cc-emergency-safety-in">
            <li>All-aboard time is on today's daily programme — ship's time, not local time. Set an alarm 60 minutes before.</li>
            <li>Carry your cabin card and a photo ID ashore; leave your passport in the cabin safe unless the line tells you otherwise.</li>
            <li>Note the port agent's number from the daily programme before leaving the ship.</li>
            {todayPort ? <li>Official travel advice for {todayPort.country || todayPort.name}: use the government links in "Before you sail" — Cruise Command does not generate port-specific safety claims.</li> : null}
          </ol>
        ) : (
          <p style={S.p} data-testid="cc-emergency-safety-post">Your voyage has ended. Keep insurer and cruise-line numbers until any claims are closed.</p>
        )}
        <p style={S.muted}>Stage derived from your voyage dates ({v.embark_date} → {v.disembark_date}). Location is never assumed from the itinerary.</p>
      </div>
    </div>
  );
}

export const PANELS = {
  medical: MedicalPanel,
  passport: PassportPanel,
  "missed-ship": MissedShipPanel,
  contacts: ContactsPanel,
  "safety-info": SafetyInfoPanel,
};
